import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Quotes, CalendarBlank, MapPin } from "@phosphor-icons/react";
import { Container, Reveal, Overline, SectionHeading, GoldButton, OutlineButton, useCollection } from "@/components/common";
import { getContent, getSiteContent } from "@/lib/api";
import { useEffect, useState } from "react";

function Hero() {
  const [hero, setHero] = useState(null);
  useEffect(() => { getSiteContent("hero").then(setHero).catch(() => {}); }, []);
  const img = hero?.portrait_image || "https://images.unsplash.com/photo-1679579664878-2c025e3f87b0?crop=entropy&cs=srgb&fm=jpg";
  return (
    <section className="relative min-h-[92vh] overflow-hidden bg-navy">
      <div className="absolute inset-0">
        <img src={img} alt="Dr. Bhawna Sirohi, Medical Oncologist" className="h-full w-full object-cover object-center opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/30" />
      </div>
      <Container className="relative flex min-h-[92vh] flex-col justify-center pt-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
          <Overline className="text-gold">Dr. Bhawna Sirohi</Overline>
          <h1 className="mt-5 font-serif text-5xl leading-[1.05] tracking-tight text-white md:text-6xl">
            {hero?.headline || "Advancing Cancer Care. Empowering Lives."}
          </h1>
          <p className="mt-5 text-lg font-light text-gold-light">
            {hero?.subheadline || "Medical Director · Senior Medical Oncologist · Researcher & Cancer Advocate"}
          </p>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300/90">
            {hero?.tagline || "Specialising in Breast and Gastrointestinal Cancers, with a career spanning India and the United Kingdom."}
          </p>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">
            {hero?.intro}
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/consultation"><button data-testid="hero-consultation" className="rounded-md bg-gold px-6 py-3 text-sm font-semibold text-navy transition-[background-color,transform] duration-200 hover:bg-gold-light hover:-translate-y-0.5">Request a Consultation</button></Link>
            <Link to="/second-opinion"><button data-testid="hero-second-opinion" className="rounded-md border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white hover:text-navy">Request a Second Opinion</button></Link>
          </div>
          <div className="mt-5 flex flex-wrap gap-6 text-sm">
            <Link to="/research" className="flex items-center gap-1.5 text-slate-300 transition-colors hover:text-gold" data-testid="hero-explore-research">Explore Research <ArrowRight size={14} /></Link>
            <Link to="/impact" className="flex items-center gap-1.5 text-slate-300 transition-colors hover:text-gold" data-testid="hero-discover-impact">Discover Her Impact <ArrowRight size={14} /></Link>
          </div>
        </motion.div>
      </Container>
    </section>
  );
}

function AboutTeaser() {
  const [intro, setIntro] = useState(null);
  useEffect(() => { getSiteContent("about_intro").then(setIntro).catch(() => {}); }, []);
  return (
    <section className="bg-white py-24">
      <Container>
        <div className="grid items-center gap-14 md:grid-cols-12">
          <Reveal className="md:col-span-7">
            <Overline className="text-gold-dark">About Dr. Sirohi</Overline>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-navy md:text-5xl">Three decades at the forefront of global oncology.</h2>
            <p className="mt-6 text-base leading-relaxed text-softgrey-600">{intro?.text}</p>
            <Link to="/about" className="mt-8 inline-block"><OutlineButton data-testid="about-teaser-cta">Read Full Biography <ArrowRight size={15} /></OutlineButton></Link>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-5">
            <div className="relative rounded-lg border border-slate-200 bg-softgrey-100 p-8">
              <Quotes size={40} weight="fill" className="text-gold/50" />
              <p className="mt-4 font-serif text-2xl leading-snug text-navy">"High-quality cancer care should reach every patient — from tertiary centres to rural villages."</p>
              <div className="mt-6 overline text-gold-dark">Dr. Bhawna Sirohi</div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function Expertise() {
  const { data } = useCollection(() => getContent("clinical_expertise"), []);
  return (
    <section className="bg-softgrey-100 py-24">
      <Container>
        <SectionHeading label="Clinical Expertise" title="Areas of specialist care" intro="Focused, evidence-based medical oncology across breast and gastrointestinal cancers, precision oncology and beyond." />
        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((c, i) => (
            <Reveal key={c.id} delay={(i % 3) * 0.06}>
              <Link to={`/clinical-expertise/${c.slug}`} data-testid={`expertise-card-${c.slug}`} className="group flex h-full flex-col bg-white p-8 transition-[background-color] duration-200 hover:bg-navy">
                <span className="overline text-gold-dark group-hover:text-gold">0{i + 1 > 9 ? i + 1 : `0${i + 1}`.slice(-2)}</span>
                <h3 className="mt-3 font-serif text-2xl text-navy transition-colors group-hover:text-white">{c.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-softgrey-600 transition-colors group-hover:text-slate-300">{c.summary}</p>
                <span className="mt-5 flex items-center gap-1.5 text-xs font-semibold text-gold-dark group-hover:text-gold">Learn more <ArrowRight size={13} /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function ResearchGlance() {
  const { data: pubs } = useCollection(() => getContent("publications", { limit: 4 }), []);
  const { data: themes } = useCollection(() => getContent("research_themes"), []);
  return (
    <section className="bg-navy py-24 text-white">
      <Container>
        <SectionHeading light label="Research at a Glance" title="A prolific, globally relevant body of research" intro="Peer-reviewed publications, national guideline leadership and international collaborations across GI and breast oncology, global health and health equity." />
        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="space-y-px overflow-hidden rounded-lg border border-white/10 bg-white/10">
              {pubs.map((p) => (
                <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="block bg-navy p-6 transition-colors hover:bg-navy-800">
                  <div className="flex items-center gap-3 text-xs text-gold"><span>{p.year}</span><span className="h-1 w-1 rounded-full bg-gold/50" /><span>{p.journal}</span></div>
                  <h4 className="mt-2 font-serif text-xl leading-snug text-white">{p.title}</h4>
                  <p className="mt-1.5 line-clamp-1 text-xs text-slate-400">{p.authors}</p>
                </a>
              ))}
            </div>
            <Link to="/research" className="mt-6 inline-block"><button data-testid="research-glance-cta" className="rounded-md bg-gold px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-gold-light">View Publications Database <ArrowRight size={14} className="inline" /></button></Link>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-white/10 p-8">
              <div className="overline text-gold">Research Themes</div>
              <div className="mt-5 flex flex-wrap gap-2">
                {themes.map((t) => (
                  <span key={t.id} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-300">{t.name}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function ImpactGlance() {
  const { data: stats } = useCollection(() => getContent("impact_stats"), []);
  return (
    <section className="bg-white py-24" data-testid="impact-dashboard">
      <Container>
        <SectionHeading label="Impact Dashboard" title="Beyond the clinic" intro="A career defined not only by clinical excellence and research, but by a deep commitment to cancer advocacy, education and health equity. All figures are administrator-managed." />
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 md:grid-cols-3 lg:grid-cols-5">
          {stats.map((s, i) => (
            <Reveal key={s.id} delay={(i % 5) * 0.05}>
              <div className="flex h-full flex-col justify-center bg-white p-8">
                <div className="font-serif text-4xl text-navy md:text-5xl">{s.value}</div>
                <div className="mt-2 text-xs font-medium uppercase tracking-wide text-softgrey-600">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8"><Link to="/impact"><OutlineButton data-testid="impact-glance-cta">Explore Her Impact & NICCI <ArrowRight size={15} /></OutlineButton></Link></div>
      </Container>
    </section>
  );
}

function InsightsTeaser() {
  const { data } = useCollection(() => getContent("insights", { limit: 3 }), []);
  if (!data.length) return null;
  return (
    <section className="bg-softgrey-100 py-24">
      <Container>
        <SectionHeading label="Latest Insights" title="Dr. Sirohi's Insights" />
        <div className="grid gap-6 md:grid-cols-3">
          {data.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.08}>
              <Link to={`/insights/${a.slug}`} className="group block overflow-hidden rounded-lg border border-slate-200 bg-white transition-transform duration-200 hover:-translate-y-1">
                {a.cover_image && <div className="aspect-[16/10] overflow-hidden"><img src={a.cover_image} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>}
                <div className="p-6">
                  <div className="overline text-gold-dark">{a.category}</div>
                  <h4 className="mt-3 font-serif text-2xl leading-snug text-navy">{a.title}</h4>
                  <p className="mt-2 line-clamp-2 text-sm text-softgrey-600">{a.excerpt}</p>
                  <div className="mt-4 text-xs text-softgrey-400">{a.reading_time}</div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function AudienceCTA() {
  const items = [
    ["Patients", "Request a consultation or second opinion.", "/consultation"],
    ["Medical Professionals", "Collaborate on research and clinical work.", "/collaborate"],
    ["Media", "Interviews, commentary & speaking.", "/contact"],
    ["Organizations", "NGO, CSR & community partnerships.", "/get-involved"],
    ["Students", "Learn, get mentored and grow.", "/education"],
  ];
  return (
    <section className="bg-navy py-24 text-white">
      <Container>
        <SectionHeading light label="Get Started" title="Find your pathway" intro="However you have arrived here, there is a clear next step." />
        <div className="grid gap-px overflow-hidden rounded-lg border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
          {items.map(([t, d, to], i) => (
            <Link key={t} to={to} data-testid={`audience-cta-${i}`} className="group flex flex-col bg-navy p-7 transition-colors hover:bg-navy-800">
              <h4 className="font-serif text-xl text-gold">{t}</h4>
              <p className="mt-2 flex-1 text-sm text-slate-400">{d}</p>
              <ArrowRight size={16} className="mt-4 text-gold transition-transform group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      </Container>
    </section>
  );
}

function EventsStrip() {
  const { data } = useCollection(() => getContent("events"), []);
  if (!data.length) return null;
  return (
    <section className="bg-softgrey-100 py-24" data-testid="home-events">
      <Container>
        <SectionHeading label="Upcoming & Recent" title="Events, talks & camps" intro="Conferences, lectures, webinars, cancer camps and important events." />
        <div className="space-y-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200">
          {data.map((e) => (
            <Reveal key={e.id}>
              <div className="flex flex-col gap-3 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                  <CalendarBlank size={22} className="mt-1 shrink-0 text-gold-dark" />
                  <div>
                    <div className="flex flex-wrap items-center gap-2 text-xs"><span className="overline text-gold-dark">{e.event_type}</span>{e.date && <span className="text-softgrey-400">· {e.date}</span>}{e.is_demo && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">DEMO</span>}</div>
                    <h4 className="mt-1 font-serif text-xl text-navy">{e.title}</h4>
                    {e.description && <p className="mt-1 max-w-xl text-sm text-softgrey-600">{e.description}</p>}
                  </div>
                </div>
                {e.location && <span className="flex items-center gap-1.5 whitespace-nowrap text-sm text-softgrey-600"><MapPin size={14} className="text-gold-dark" /> {e.location}</span>}
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <AboutTeaser />
      <Expertise />
      <ResearchGlance />
      <ImpactGlance />
      <EventsStrip />
      <InsightsTeaser />
      <AudienceCTA />
    </div>
  );
}
